import React from 'react';
import {
  AlertOutlined,
  BulbOutlined,
  ClusterOutlined,
  DatabaseOutlined,
  DeploymentUnitOutlined,
  ExperimentOutlined,
  FileSearchOutlined,
  FilterOutlined,
  HeartOutlined,
  MedicineBoxOutlined,
  ReconciliationOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  ScanOutlined,
  SkinOutlined,
  SmileOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';

/**
 * SINGLE SOURCE OF TRUTH for Xoe expert persona icons.
 *
 * An admin expert template stores its icon as a free-text antd-style kebab-case NAME
 * (e.g. "medicine-box"), seeded by GlobalXoeExpertSeeder (admin actionable-api). This module maps
 * those names to the actual AntD components and exposes getExpertIconComponent, which always returns
 * a renderable icon component — unknown, empty, or arbitrary free-text names fall back to the robot
 * icon so an icon-name string is never rendered as raw text. Add new expert icons here.
 */
const EXPERT_ICON_COMPONENTS: Record<string, React.ComponentType<any>> = {
  'user-md': UserOutlined, // AntD has no medical-person glyph — closest equivalent
  'medicine-box': MedicineBoxOutlined,
  experiment: ExperimentOutlined,
  trophy: TrophyOutlined,
  'deployment-unit': DeploymentUnitOutlined,
  'file-search': FileSearchOutlined,
  thunderbolt: ThunderboltOutlined,
  heart: HeartOutlined,
  alert: AlertOutlined,
  scan: ScanOutlined,
  tool: ToolOutlined,
  'safety-certificate': SafetyCertificateOutlined,
  database: DatabaseOutlined,
  bulb: BulbOutlined,
  smile: SmileOutlined,
  skin: SkinOutlined,
  cluster: ClusterOutlined,
  filter: FilterOutlined,
  reconciliation: ReconciliationOutlined,
};

/** Used for unknown, empty, or arbitrary free-text icon names. */
const FALLBACK_ICON: React.ComponentType<any> = RobotOutlined;

/**
 * Resolve an expert's stored icon name to its AntD component. Returns the robot fallback for any
 * unknown, empty, or null name so callers can always render a real icon component.
 */
export const getExpertIconComponent = (
  icon: string | null | undefined,
): React.ComponentType<any> => (icon && EXPERT_ICON_COMPONENTS[icon]) || FALLBACK_ICON;
